import { dayIso, tripStartOf } from "../components/trip-planner/trip-date";
import type { Trip, TripDay, TripStayRef } from "../types";
import { dayCity, tripRoute } from "./trip-route";
import type { TripParty } from "./trip-party";

/**
 * Where the guest sleeps (owner, 2026-11-01).
 *
 * One stay per city of the route: the card sits at the start of that city's
 * days and covers its nights. A night belongs to a day (`TripDay.stay` is
 * where you sleep after that day), so the stay follows the days when the
 * route changes and a check-in and check-out are simply the first night's
 * date and the day after the last.
 *
 * The trip's last day is the day you leave: it has no night. A last city of
 * one day therefore needs no stay and shows none.
 *
 * Every stay is paid online, in full (owner rule, 2026-09-25): a hotel on its
 * own page, a camp or villa by the link the team emails. So a stay never goes
 * in the bucket and never counts in what is due on the day.
 *
 * Pure: web and mobile both import it.
 */

/** One city of the route and the nights it needs. */
export interface TripStayLeg {
  readonly city: string;
  /** Every day in this city, in order. */
  readonly dayIds: readonly string[];
  /** The days that end with a night here: all of them but the trip's last day. */
  readonly nightDayIds: readonly string[];
  readonly nights: number;
  /** YYYY-MM-DD of the first night, and of the morning after the last. */
  readonly checkin: string;
  readonly checkout: string;
  /** The stay on those nights, when one is chosen (the first night's). */
  readonly stay: TripStayRef | null;
}

/** The trip's route, city by city, with its nights and chosen stays. */
export function tripStayLegs(trip: Trip): TripStayLeg[] {
  const start = tripStartOf(trip);
  const lastIndex = trip.days.length - 1;
  const legs: TripStayLeg[] = [];
  let i = 0;
  for (const leg of tripRoute(trip)) {
    const days = trip.days.slice(i, i + leg.days);
    const firstIndex = i;
    i += leg.days;
    const nightDays = days.filter((_, j) => firstIndex + j < lastIndex);
    const nights = nightDays.length;
    legs.push({
      city: leg.city,
      dayIds: days.map((d) => d.id),
      nightDayIds: nightDays.map((d) => d.id),
      nights,
      checkin: dayIso(start, firstIndex),
      checkout: dayIso(start, firstIndex + nights),
      stay: nightDays.find((d) => d.stay)?.stay ?? null,
    });
  }
  return legs;
}

/** The leg a day belongs to. */
export function stayLegOfDay(trip: Trip, dayId: string): TripStayLeg | null {
  return tripStayLegs(trip).find((leg) => leg.dayIds.includes(dayId)) ?? null;
}

/**
 * Put a stay on a city's nights (or take it off, with null). Only the nights
 * of that leg change.
 */
export function setLegStay(trip: Trip, nightDayIds: readonly string[], stay: TripStayRef | null): Trip {
  const ids = new Set(nightDayIds);
  if (ids.size === 0) return trip;
  return {
    ...trip,
    days: trip.days.map((d) => {
      if (!ids.has(d.id)) return d;
      if (stay) return { ...d, stay };
      const { stay: _drop, ...rest } = d;
      return rest;
    }),
  };
}

/**
 * Drop a stay that no longer fits its day: a day moved to another city keeps
 * no hotel from the old one. Run after the route changes.
 */
export function pruneStays(trip: Trip): Trip {
  let changed = false;
  const days = trip.days.map((d): TripDay => {
    if (!d.stay || d.stay.citySlug === dayCity(trip, d)) return d;
    changed = true;
    const { stay: _drop, ...rest } = d;
    return rest;
  });
  return changed ? { ...trip, days } : trip;
}

/** Whether a catalogue row can be a stay: hotels, riads, villas and camps. */
export function isStayItem(
  item: { mode?: string | null; segment?: string | null; categorySlug?: string | null } | null,
): boolean {
  if (!item || item.mode !== "bookable") return false;
  return item.segment === "stays" || item.categorySlug === "camp";
}

/**
 * The web address of a hotel with the trip's dates and party filled in, as
 * its page reads them (`stays.$city.$slug`).
 */
export function hotelStayHref(
  lang: string,
  stay: TripStayRef,
  leg: Pick<TripStayLeg, "checkin" | "checkout">,
  party: TripParty,
): string {
  const q = new URLSearchParams({
    checkin: leg.checkin,
    checkout: leg.checkout,
    adults: String(party.adults),
  });
  if (party.childAges.length > 0) q.set("childAges", party.childAges.join(","));
  return `/${lang}/stays/${stay.citySlug}/${stay.slug}?${q.toString()}`;
}

/** Most characters kept in an own stay's name and address. */
const OWN_NAME_MAX = 80;
const OWN_ADDRESS_MAX = 160;

/**
 * A stay the guest arranged themselves, for one city. Null when the name is
 * empty: a stay is only worth a card if it has a name.
 */
export function ownStay(city: string, name: string, address?: string): TripStayRef | null {
  const title = name.trim().replace(/\s+/g, " ").slice(0, OWN_NAME_MAX);
  if (!title) return null;
  const where = address?.trim().replace(/\s+/g, " ").slice(0, OWN_ADDRESS_MAX);
  return { kind: "own", itemId: "", slug: "", citySlug: city, title, ...(where ? { address: where } : {}) };
}
